import React, { useState } from "react";
import { stripHTMLTags } from "../../utils/helper";

const READ_MORE_LINK_CLASS = "read-more-less-link";

const truncateHTML = (html: string, limit: number): string => {
  if (!html) return "";

  let htmlIndex = 0;
  let textCount = 0;

  while (htmlIndex < html.length && textCount < limit) {
    if (html[htmlIndex] === "<") {
      const tagEnd = html.indexOf(">", htmlIndex);
      if (tagEnd !== -1) {
        htmlIndex = tagEnd + 1;
      } else {
        break;
      }
    } else {
      textCount++;
      htmlIndex++;
    }
  }

  return `${html.substring(0, htmlIndex).trim()}...`;
};

const createToggleLink = (expanded: boolean) =>
  `<span class="${READ_MORE_LINK_CLASS}">${
    expanded ? "" : " See more"
  }</span>`;

/** Insert toggle link inside the outermost open block so it sits after the last word. */
const injectBeforeLastClosingTag = (html: string, snippet: string): string => {
  const closingTags = html.match(/<\/[a-zA-Z][a-zA-Z0-9]*>/g);
  if (!closingTags?.length) {
    return html + snippet;
  }

  const lastTag = closingTags[closingTags.length - 1];
  const idx = html.lastIndexOf(lastTag);
  if (idx === -1) {
    return html + snippet;
  }

  return html.slice(0, idx) + snippet + html.slice(idx);
};

const buildHtmlWithToggle = (
  text: string,
  expanded: boolean,
  shouldTruncate: boolean,
  textLimit: number
): string => {
  if (!shouldTruncate) {
    return text;
  }

  const link = createToggleLink(expanded);

  if (expanded) {
    return injectBeforeLastClosingTag(text, link);
  }

  // Truncated HTML may end mid-element; append so the link follows the last visible word.
  return truncateHTML(text, textLimit) + link;
};

export default function ReadMoreLess({
  text,
  textLimit = 200,
}: {
  text: string;
  textLimit?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const plainText = stripHTMLTags(text || "") || "";
  const shouldTruncate = plainText.length > textLimit;
  const displayHtml = buildHtmlWithToggle(
    text,
    isExpanded,
    shouldTruncate,
    textLimit
  );

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.closest(`.${READ_MORE_LINK_CLASS}`)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className="read-more-less"
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: displayHtml }}
    />
  );
}
